import { useEffect, useState } from "react";
import { useQuery } from "react-query";
import { useSession } from "ui/containers/session";
import { createContainer } from "unstated-next";

interface VersionData {
  version: string;
  changelog: string;
}

const useContainer = () => {
  const { session } = useSession();
  const [version, setVersion] = useState<string>("");
  const [changelog, setChangelog] = useState<string>("");

  const getLatestVersion = (): Promise<VersionData> => {
    return new Promise((resolve, reject) => {
      return fetch("/api/authenticated/version")
        .then((response) => {
          if (response.status !== 200) {
            return reject(new Error("failed to get latest version"));
          }
          response.json().then((data: VersionData) => resolve(data));
        })
        .catch((error: Error) => {
          console.error(error);
          reject(new Error("failed to get latest version"));
        });
    });
  };

  const { data, error } = useQuery<VersionData | undefined>(
    session ? "/api/authenticated/version" : null,
    getLatestVersion,
    {
      refetchOnMount: true,
      refetchOnWindowFocus: true,
      retry: 2,
      onError: (err) => {
        console.error("Version check failed:", err);
      },
    }
  );

  useEffect(() => {
    if (!data) return;
    // Safely extract values with null checks
    const latestVersion = data.version;
    const changelogText = data.changelog || "";

    if (latestVersion) {
      setVersion((version) => (version ? version : latestVersion));
      setChangelog(changelogText);
    }
  }, [data]);

  return {
    version,
    changelog: changelog || "",
    latestVersion: data?.version,
    getLatestVersion,
    error: error ? true : false,
  };
};

export const { useContainer: useVersion, Provider: VersionProvider } =
  createContainer(useContainer);
